import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import {
  Button,
  Card,
  IconButton,
  CardContent,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Alert } from "@material-ui/lab";
import { Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import AppBarComponent from "../../layout/AppBar";
import Scrollbar from "react-scrollbars-custom";
import DeviceDataService from "../../../services/DeviceService";

const drawerWidth = 280;

const DEFAULT_LOCATION = 1;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: "100vh",
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  card: {
    borderRadius: "4px",
  },
  cardContent: {
    padding: theme.spacing(2),
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  tableWrap: {
    overflowX: "auto",
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  formField: {
    marginBottom: theme.spacing(2),
    minWidth: "100%",
  },
  warningAlert: {
    marginBottom: theme.spacing(2),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
  },
  helpNote: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  helpText: {
    whiteSpace: "pre-line",
    marginBottom: theme.spacing(2),
  },
}));

export default function DeviceSetup() {
  const classes = useStyles();
  const { t } = useTranslation();
  const level = localStorage.getItem("level");
  const canAccess =
    level === "super" || level === "admin" || level === "tech_support";

  const [unmapped, setUnmapped] = useState([]);
  const [known, setKnown] = useState([]);
  const [existingDevices, setExistingDevices] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [helpOpen, setHelpOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [selectedAlive, setSelectedAlive] = useState(null);
  const [saving, setSaving] = useState(false);

  const [addForm, setAddForm] = useState({
    name: "",
    type: "",
  });
  const [replaceTarget, setReplaceTarget] = useState(null);

  useEffect(() => {
    DeviceDataService.adminAll()
      .then((response) => setExistingDevices(response.data || []))
      .catch(() => {
        showSnackbar(t("deviceSetup.loadDevicesError"), "error");
      });
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      const response = await DeviceDataService.discover();
      setUnmapped(response.data.unmapped || []);
      setKnown(response.data.known || []);
      showSnackbar(
        t("deviceSetup.scanSuccess", {
          unmapped: (response.data.unmapped || []).length,
          known: (response.data.known || []).length,
        }),
        "success"
      );
      const devicesRes = await DeviceDataService.adminAll();
      setExistingDevices(devicesRes.data || []);
    } catch (err) {
      showSnackbar(
        (err.response && err.response.data && err.response.data.message) ||
          t("deviceSetup.scanError"),
        "error"
      );
    } finally {
      setScanning(false);
    }
  };

  const openAdd = (alive) => {
    setSelectedAlive(alive);
    setAddForm({
      name: alive.name || "",
      type: alive.type || "",
    });
    setAddOpen(true);
  };

  const openReplace = (alive) => {
    setSelectedAlive(alive);
    setReplaceTarget(null);
    setReplaceOpen(true);
  };

  const handleAdd = async () => {
    if (!selectedAlive || !addForm.name || !addForm.type) {
      showSnackbar(t("deviceSetup.addValidation"), "error");
      return;
    }
    setSaving(true);
    try {
      await DeviceDataService.mapDevice({
        mac: selectedAlive.mac,
        name: addForm.name,
        type: addForm.type,
        location: DEFAULT_LOCATION,
      });
      showSnackbar(t("deviceSetup.addSuccess"), "success");
      setAddOpen(false);
      await handleScan();
    } catch (err) {
      showSnackbar(
        (err.response && err.response.data && err.response.data.message) ||
          t("deviceSetup.addError"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReplace = async () => {
    if (!selectedAlive || !replaceTarget) {
      showSnackbar(t("deviceSetup.replaceValidation"), "error");
      return;
    }
    setSaving(true);
    try {
      await DeviceDataService.replaceDevice(replaceTarget.id, {
        mac: selectedAlive.mac,
      });
      showSnackbar(t("deviceSetup.replaceSuccess"), "success");
      setReplaceOpen(false);
      await handleScan();
    } catch (err) {
      showSnackbar(
        (err.response && err.response.data && err.response.data.message) ||
          t("deviceSetup.replaceError"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!canAccess) {
    return <Redirect to="/rooms" />;
  }

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBarComponent />
      <main className={classes.content}>
        <Scrollbar style={{ width: "100%", height: "100%" }}>
          <div className={classes.appBarSpacer} />
          <Container maxWidth="lg" className={classes.container}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <div className={classes.toolbarRow}>
                      <div>
                        <div className={classes.titleRow}>
                          <Typography variant="h5" component="h1">
                            {t("deviceSetup.title")}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label={t("deviceSetup.helpTitle")}
                            onClick={() => setHelpOpen(true)}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </div>
                        <Typography variant="body2" color="textSecondary">
                          {t("deviceSetup.subtitle")}
                        </Typography>
                      </div>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleScan}
                        disabled={scanning}
                        startIcon={
                          scanning ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : null
                        }
                      >
                        {scanning
                          ? t("deviceSetup.scanning")
                          : t("deviceSetup.scan")}
                      </Button>
                    </div>

                    {known.length > 0 && (
                      <Alert severity="info" className={classes.warningAlert}>
                        {t("deviceSetup.knownInfo", { count: known.length })}
                      </Alert>
                    )}

                    <div className={classes.tableWrap}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("deviceSetup.mac")}</TableCell>
                            <TableCell>{t("deviceSetup.ip")}</TableCell>
                            <TableCell>{t("deviceSetup.fwType")}</TableCell>
                            <TableCell>{t("deviceSetup.actions")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {unmapped.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4}>
                                {t("deviceSetup.empty")}
                              </TableCell>
                            </TableRow>
                          ) : (
                            unmapped.map((row) => (
                              <TableRow key={row.mac}>
                                <TableCell>{row.mac}</TableCell>
                                <TableCell>{row.ip || "—"}</TableCell>
                                <TableCell>{row.type || "—"}</TableCell>
                                <TableCell className={classes.actionsCell}>
                                  <Button
                                    className={classes.actionButton}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => openAdd(row)}
                                  >
                                    {t("deviceSetup.add")}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openReplace(row)}
                                  >
                                    {t("deviceSetup.replace")}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Scrollbar>
      </main>

      <Dialog
        open={addOpen}
        onClose={() => !saving && setAddOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("deviceSetup.addTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("deviceSetup.addDescription", {
              mac: (selectedAlive && selectedAlive.mac) || "",
            })}
          </DialogContentText>
          <TextField
            className={classes.formField}
            label={t("deviceSetup.name")}
            value={addForm.name}
            onChange={(e) => {
              const name = e.target.value;
              setAddForm((prev) => ({ ...prev, name: name }));
            }}
            fullWidth
            required
          />
          <TextField
            className={classes.formField}
            label={t("deviceSetup.type")}
            value={addForm.type}
            fullWidth
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>
            {t("deviceSetup.cancel")}
          </Button>
          <Button
            onClick={handleAdd}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? t("deviceSetup.saving") : t("deviceSetup.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={replaceOpen}
        onClose={() => !saving && setReplaceOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("deviceSetup.replaceTitle")}</DialogTitle>
        <DialogContent>
          <Autocomplete
            className={classes.formField}
            options={existingDevices}
            getOptionLabel={(option) =>
              option
                ? `${option.name || ""} (${option.mac || ""}) — ${
                    option.type || ""
                  }`
                : ""
            }
            value={replaceTarget}
            onChange={(_, value) => setReplaceTarget(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("deviceSetup.existingDevice")}
                required
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceOpen(false)} disabled={saving}>
            {t("deviceSetup.cancel")}
          </Button>
          <Button
            onClick={handleReplace}
            color="primary"
            variant="contained"
            disabled={saving || !replaceTarget}
          >
            {saving ? t("deviceSetup.saving") : t("deviceSetup.confirmReplace")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("deviceSetup.helpTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.helpNote}>
            {t("deviceSetup.helpNote")}
          </Typography>
          <Typography variant="subtitle1">
            {t("deviceSetup.helpAddTitle")}
          </Typography>
          <Typography variant="body2" className={classes.helpText}>
            {t("deviceSetup.helpAdd")}
          </Typography>
          <Typography variant="subtitle1">
            {t("deviceSetup.helpReplaceTitle")}
          </Typography>
          <Typography variant="body2" className={classes.helpText}>
            {t("deviceSetup.helpReplace")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)} color="primary">
            {t("deviceSetup.helpClose")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
